import { classNames } from "../App";
import CheckIcon from "../icons/check";

// The rules a password has to satisfy, as a checklist that fills in while you
// type instead of three separate errors after you submit.
//
// These mirror the server's own check in models/password.go, and that is the
// whole point of the file: the server decides, so whatever the form says has to
// agree with it. The first version did not, in three ways, all of them visible
// to the person typing:
//
//   - Length counted UTF-16 code units, so "Abcde😀!" looked like 8 characters
//     here and 7 runes to the server: all three rules went green and the save
//     came back 400.
//   - /[A-Z]/ does not match Ă or Ș, which unicode.IsUpper accepts, so
//     "Ăbcdefg!" was blocked here and would have been accepted there.
//   - The special character was matched against a fixed list of ASCII
//     punctuation, so "Abcdefg€" was blocked here and accepted there.
//
// Spreading the string counts code points, which is what Go counts as runes;
// \p{Lu} is unicode.IsUpper; \p{P} and \p{S} are unicode.IsPunct and
// unicode.IsSymbol. A space falls in none of those classes, so both ends refuse
// it — which is the one case that already agreed.
export const passwordRuleChecks = [
    { key: "length", test: (value) => [...value].length >= 8 },
    { key: "uppercase", test: (value) => /\p{Lu}/u.test(value) },
    { key: "special", test: (value) => /[\p{P}\p{S}]/u.test(value) },
]

// Returns the (name, message, test) triple yup's .test() takes, so a schema can
// spread one straight into its chain:
//
//     .test(...passwordRule("uppercase", "Cel putin o litera mare"))
//
// The point is that the checklist a person reads and the validation that
// actually blocks the submit button run the same three functions and cannot
// drift apart. Each app keeps its own wording.
//
// An empty value passes every rule and is left to .required(), so a blank field
// reports "this field is required" rather than three complaints about a
// password nobody has typed yet.
export function passwordRule(key, message) {
    const rule = passwordRuleChecks.find((r) => r.key === key)
    if (!rule) throw new Error(`unknown password rule: ${key}`)

    return [key, message, (value) => !value || rule.test(value)]
}

// labels maps each rule key to the sentence to show for it, so the apps that
// translate can pass t(...) and the two that do not can pass Romanian.
export default function PasswordRules({ password = "", labels, className }) {

    const value = password || ""

    return (
        <ul className={classNames(className, "mt-2 flex flex-col gap-1")}>
            {passwordRuleChecks.map((rule) => {
                const met = rule.test(value)

                return (
                    <li key={rule.key} className="flex flex-row items-center gap-2">
                        <span
                            className={classNames(
                                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors",
                                met ? "border-accent_green bg-accent_green" : "border-neutral-border bg-white"
                            )}
                        >
                            {met && <CheckIcon className="h-2.5 w-2.5 fill-white" />}
                        </span>
                        <span className={classNames("font-text text-sm", met ? "text-neutral-1100" : "text-neutral-400")}>
                            {labels[rule.key]}
                        </span>
                    </li>
                )
            })}
        </ul>
    )
}
